import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RowGroup } from '../NodeListItemGroup';
import { ListCreator } from './useScorableItemFilters.d';
import { CheckboxState, getParentOfNode, listCreatorFactory } from './selection-utils';

type ScorableItem = NonNullable<RowGroup['scorableItems']>[number];

interface DataToDetermineStateOfNodeBySubTree {
  newNodeStates: Record<string, CheckboxState>;
  newScorableItemStates: Record<string, CheckboxState>;
}

interface Options {
  filteredNodeTrees: RowGroup[];
  allNodeTrees: RowGroup[];
  setListCreator: Dispatch<SetStateAction<ListCreator | undefined>>;
}

export default function useScorableItemSelection({ filteredNodeTrees, allNodeTrees, setListCreator }: Options) {
  // key/val object containing checkbox state of scorable item
  const [scorableItemStates, setScorableItemStates] = useState<Record<string, CheckboxState>>({});
  const newScorableItemStatesRef = useRef<typeof scorableItemStates | null>(null);

  // key/val object containing checkbox state of node
  const [nodeStates, setNodeStates] = useState<Record<string, CheckboxState>>({});
  const newNodeStatesRef = useRef<typeof nodeStates | null>(null);

  const flattenScorableItems = useCallback((node: RowGroup): ScorableItem[] => {
    return [...(node.scorableItems || []), ...(node.nodes?.flatMap(flattenScorableItems) || [])];
  }, []);

  const flattenNodes = useCallback((node: RowGroup): RowGroup[] => {
    return [node, ...(node.nodes?.flatMap(flattenNodes) || [])];
  }, []);

  const allNodes = useMemo(() => {
    return allNodeTrees.flatMap(node => flattenNodes(node));
  }, [allNodeTrees, flattenNodes]);

  const allFilteredNodes = useMemo(() => {
    return filteredNodeTrees.flatMap(node => flattenNodes(node));
  }, [filteredNodeTrees, flattenNodes]);

  const allScorableItems = useMemo(() => {
    return allNodeTrees.flatMap(node => flattenScorableItems(node));
  }, [allNodeTrees, flattenScorableItems]);

  const allFilteredScorableItems = useMemo(() => {
    return filteredNodeTrees.flatMap(node => flattenScorableItems(node));
  }, [filteredNodeTrees, flattenScorableItems]);

  useEffect(() => {
    const scorableItems = allNodeTrees.flatMap(node => flattenScorableItems(node));
    const nodes = allNodeTrees.flatMap(node => flattenNodes(node));

    const scorableItemsCheckedStates = scorableItems.reduce<Record<string, CheckboxState>>((acc, item) => {
      acc[item.id] = 'unchecked';

      return acc;
    }, {});

    const nodesCheckedStates = nodes.reduce<Record<string, CheckboxState>>((acc, node) => {
      acc[node.id] = 'unchecked';

      return acc;
    }, {});

    setScorableItemStates(scorableItemsCheckedStates);
    setNodeStates(nodesCheckedStates);
  }, [flattenNodes, flattenScorableItems, allNodeTrees]);

  const determineStateOfNodeBySubTree = useCallback(
    (node: RowGroup, { newNodeStates, newScorableItemStates }: DataToDetermineStateOfNodeBySubTree) => {
      let nodeState: CheckboxState = 'indeterminate';

      if (!node.nodes && node.scorableItems) {
        const allItemsChecked = node.scorableItems.every(item => newScorableItemStates[item.id] === 'checked');
        const allItemsUnchecked = node.scorableItems.every(item => newScorableItemStates[item.id] === 'unchecked');

        if (allItemsChecked) {
          nodeState = 'checked';
        } else if (allItemsUnchecked) {
          nodeState = 'unchecked';
        }
      } else if (!node.scorableItems && node.nodes) {
        const allNodesChecked = node.nodes.every(childNode => newNodeStates[childNode.id] === 'checked');
        const allNodesUnchecked = node.nodes.every(childNode => newNodeStates[childNode.id] === 'unchecked');

        if (allNodesChecked) {
          nodeState = 'checked';
        } else if (allNodesUnchecked) {
          nodeState = 'unchecked';
        }
      } else if (node.nodes && node.scorableItems) {
        const allItemsChecked = node.scorableItems.every(item => newScorableItemStates[item.id] === 'checked');
        const allItemsUnchecked = node.scorableItems.every(item => newScorableItemStates[item.id] === 'unchecked');
        const allNodesChecked = node.nodes.every(childNode => newNodeStates[childNode.id] === 'checked');
        const allNodesUnchecked = node.nodes.every(childNode => newNodeStates[childNode.id] === 'unchecked');

        if (allItemsChecked && allNodesChecked) {
          nodeState = 'checked';
        } else if (allItemsUnchecked && allNodesUnchecked) {
          nodeState = 'unchecked';
        }
      }

      return nodeState;
    },
    [],
  );

  useEffect(() => {
    setNodeStates(prev => {
      if (Object.keys(scorableItemStates).length === 0) return prev;

      function flatMapNodesBottomUp(node: RowGroup, flatNodes: RowGroup[] = []): RowGroup[] {
        if (node.nodes) {
          node.nodes.forEach(childNode => flatMapNodesBottomUp(childNode, flatNodes));
        }

        flatNodes.push(node);

        return flatNodes;
      }

      const bottomUpNodesInTree = filteredNodeTrees.flatMap(node => flatMapNodesBottomUp(node));

      const newStates = bottomUpNodesInTree.reduce<Record<string, CheckboxState>>(
        (acc, node) => {
          const newState = determineStateOfNodeBySubTree(node, {
            newNodeStates: acc,
            newScorableItemStates: scorableItemStates,
          });

          acc[node.id] = newState;

          return acc;
        },
        { ...prev },
      );

      return newStates;
    });
  }, [determineStateOfNodeBySubTree, filteredNodeTrees, flattenNodes, flattenScorableItems, scorableItemStates]);

  const findNodeInFilteredTree = useCallback(
    (id: string): RowGroup | null => {
      return allFilteredNodes.find(node => node.id === id) || null;
    },
    [allFilteredNodes],
  );

  const updateNodeAndChildren = useCallback((node: RowGroup, state: CheckboxState) => {
    if (!newScorableItemStatesRef.current || !newNodeStatesRef.current) return;

    newNodeStatesRef.current[node.id] = state;

    (node.scorableItems || []).forEach(item => {
      newScorableItemStatesRef.current![item.id] = state;
    });

    node.nodes?.forEach(childNode => updateNodeAndChildren(childNode, state));
  }, []);

  const determineStateOfNodeByScorableItems = useCallback((scorableItemSates: CheckboxState[]): CheckboxState => {
    const checkedCount = scorableItemSates.filter(state => state === 'checked').length;
    const indeterminateCount = scorableItemSates.filter(state => state === 'indeterminate').length;

    if (indeterminateCount > 0) return 'indeterminate';
    if (checkedCount === scorableItemSates.length) return 'checked';
    if (checkedCount === 0) return 'unchecked';
    return 'indeterminate';
  }, []);

  const updateNodeChain = useCallback(
    (node: RowGroup) => {
      const scorableItems = flattenScorableItems(node);
      const states = scorableItems
        .map(scorableItem => newScorableItemStatesRef.current?.[scorableItem.id])
        .filter((state): state is CheckboxState => state !== undefined);

      if (states.length !== scorableItems.length) return;

      const newNodeState = determineStateOfNodeByScorableItems(states);

      if (!newNodeStatesRef.current || scorableItemStates[node.id] === newNodeState) return;

      newNodeStatesRef.current[node.id] = newNodeState;
    },
    [determineStateOfNodeByScorableItems, flattenScorableItems, scorableItemStates],
  );

  const updateParentChain = useCallback(
    (childNode: RowGroup, { newNodeStates, newScorableItemStates }: DataToDetermineStateOfNodeBySubTree) => {
      const parentNode = getParentOfNode(childNode.id, allFilteredNodes);
      if (!parentNode || !newNodeStatesRef.current) return;

      newNodeStatesRef.current[parentNode.id] = determineStateOfNodeBySubTree(parentNode, {
        newNodeStates,
        newScorableItemStates,
      });

      updateParentChain(parentNode, { newNodeStates, newScorableItemStates });
    },
    [allFilteredNodes, determineStateOfNodeBySubTree],
  );

  // optimization possible by storing a key/value pair of the scorableItemId and its node object
  const getNodeOfScorableItem = useCallback(
    (id: string): RowGroup | null => {
      const nodeId = allFilteredScorableItems.find(item => item.id === id)?.nodeId;
      if (!nodeId) return null;
      return allFilteredNodes.find(node => node.id === nodeId) || null;
    },
    [allFilteredNodes, allFilteredScorableItems],
  );

  const setNodeState = useCallback(
    (id: string, state: CheckboxState) => {
      if (newScorableItemStatesRef.current) return;

      const node = findNodeInFilteredTree(id);
      if (!node) return;

      newScorableItemStatesRef.current = { ...scorableItemStates };
      newNodeStatesRef.current = { ...nodeStates };

      if (state !== 'indeterminate') {
        updateNodeAndChildren(node, state);
      }

      updateParentChain(node, {
        newNodeStates: newNodeStatesRef.current,
        newScorableItemStates: newScorableItemStatesRef.current,
      });

      setScorableItemStates(newScorableItemStatesRef.current);
      setNodeStates(newNodeStatesRef.current);
      const listCreator = listCreatorFactory({
        newScorableItemsStates: newScorableItemStatesRef.current,
        allScorableItems,
        allNodes,
        filteredNodes: allFilteredNodes,
      });
      setListCreator(() => listCreator);

      newScorableItemStatesRef.current = null;
      newNodeStatesRef.current = null;
    },
    [
      allFilteredNodes,
      allNodes,
      allScorableItems,
      findNodeInFilteredTree,
      nodeStates,
      scorableItemStates,
      setListCreator,
      updateNodeAndChildren,
      updateParentChain,
    ],
  );

  const setScorableItemState = useCallback(
    (id: string, state: CheckboxState) => {
      if (newScorableItemStatesRef.current || newNodeStatesRef.current) return;

      newScorableItemStatesRef.current = { ...scorableItemStates, [id]: state };

      const node = getNodeOfScorableItem(id);
      if (!node) {
        newScorableItemStatesRef.current = null;
        return;
      }

      newNodeStatesRef.current = { ...nodeStates };

      updateNodeChain(node);
      updateParentChain(node, {
        newNodeStates: newNodeStatesRef.current,
        newScorableItemStates: newScorableItemStatesRef.current,
      });

      setScorableItemStates(newScorableItemStatesRef.current);
      setNodeStates(newNodeStatesRef.current);
      const listCreator = listCreatorFactory({
        newScorableItemsStates: newScorableItemStatesRef.current,
        allNodes,
        allScorableItems,
        filteredNodes: allFilteredNodes,
      });
      setListCreator(() => listCreator);

      newScorableItemStatesRef.current = null;
      newNodeStatesRef.current = null;
    },
    [
      allFilteredNodes,
      allNodes,
      allScorableItems,
      getNodeOfScorableItem,
      nodeStates,
      scorableItemStates,
      setListCreator,
      updateNodeChain,
      updateParentChain,
    ],
  );

  function selectAllItems() {
    newScorableItemStatesRef.current = { ...scorableItemStates };
    newNodeStatesRef.current = { ...nodeStates };

    allFilteredScorableItems.forEach(item => {
      newScorableItemStatesRef.current![item.id] = 'checked';
    });
    allFilteredNodes.forEach(node => {
      newNodeStatesRef.current![node.id] = 'checked';
    });

    setScorableItemStates(newScorableItemStatesRef.current);
    setNodeStates(newNodeStatesRef.current);
    const listCreator = listCreatorFactory({
      newScorableItemsStates: newScorableItemStatesRef.current,
      allNodes,
      allScorableItems,
      filteredNodes: allFilteredNodes,
    });
    setListCreator(() => listCreator);
    newScorableItemStatesRef.current = null;
    newNodeStatesRef.current = null;
  }

  function clearAllItems() {
    newScorableItemStatesRef.current = { ...scorableItemStates };
    newNodeStatesRef.current = { ...nodeStates };

    allFilteredScorableItems.forEach(item => {
      newScorableItemStatesRef.current![item.id] = 'unchecked';
    });

    allFilteredNodes.forEach(node => {
      newNodeStatesRef.current![node.id] = 'unchecked';
    });

    setScorableItemStates(newScorableItemStatesRef.current);
    setNodeStates(newNodeStatesRef.current);
    const listCreator = listCreatorFactory({
      newScorableItemsStates: newScorableItemStatesRef.current,
      allNodes,
      allScorableItems,
      filteredNodes: allFilteredNodes,
    });
    setListCreator(() => listCreator);
    newScorableItemStatesRef.current = null;
    newNodeStatesRef.current = null;
  }

  const getState = useCallback(
    (id: string): CheckboxState => {
      return scorableItemStates[id] || nodeStates[id] || 'unchecked';
    },
    [nodeStates, scorableItemStates],
  );

  const hasSelectedItems = useMemo(() => {
    return allFilteredNodes.some(node => nodeStates[node.id] !== 'unchecked');
  }, [allFilteredNodes, nodeStates]);

  return {
    result: Object.entries({ ...nodeStates, ...scorableItemStates })
      .filter(([, state]) => state === 'checked' || state === 'indeterminate')
      .map(([id]) => id),
    hasSelectedItems,
    getCheckboxState: getState,
    setNodeCheckboxState: setNodeState,
    setScorableItemCheckboxState: setScorableItemState,
    selectAllItems,
    clearAllItems,
  };
}
